if (!customElements.get('scene-hotspot-carousel')) {
  customElements.define(
    'scene-hotspot-carousel',
    class SceneHotspotCarousel extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.track = this.querySelector('[data-scene-track]');
        this.viewport = this.querySelector('[data-scene-viewport]');
        this.slides = Array.from(this.querySelectorAll('[data-scene-slide]'));
        this.dots = Array.from(this.querySelectorAll('[data-scene-dot]'));
        this.previousButton = this.querySelector('[data-scene-previous]');
        this.nextButton = this.querySelector('[data-scene-next]');
        this.status = this.querySelector('[data-scene-status]');
        this.currentIndex = Math.max(
          0,
          this.slides.findIndex((slide) => slide.classList.contains('is-active'))
        );
        this.pointerStartX = null;
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.mobileMedia = window.matchMedia('(max-width: 749px)');
        this.autoplayEnabled = this.dataset.autoplay === 'true' && !this.reduceMotion;
        this.autoplayDelay = Math.max(Number(this.dataset.autoplayDelay) || 5000, 2000);

        if (!this.track || !this.slides.length) return;

        this.onPrevious = () => this.goTo(this.currentIndex - 1, true);
        this.onNext = () => this.goTo(this.currentIndex + 1, true);
        this.onKeydown = this.handleKeydown.bind(this);
        this.onPointerDown = this.handlePointerDown.bind(this);
        this.onPointerUp = this.handlePointerUp.bind(this);
        this.onDocumentClick = this.handleDocumentClick.bind(this);
        this.onVisibilityChange = this.handleVisibilityChange.bind(this);
        this.onBlockSelect = this.handleBlockSelect.bind(this);
        this.onBreakpointChange = this.handleBreakpointChange.bind(this);

        this.previousButton?.addEventListener('click', this.onPrevious);
        this.nextButton?.addEventListener('click', this.onNext);
        this.addEventListener('keydown', this.onKeydown);
        this.viewport?.addEventListener('pointerdown', this.onPointerDown);
        this.viewport?.addEventListener('pointerup', this.onPointerUp);
        this.viewport?.addEventListener('pointercancel', this.onPointerUp);
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        this.mobileMedia.addEventListener?.('change', this.onBreakpointChange);

        this.dots.forEach((dot, index) => {
          dot.addEventListener('click', () => this.goTo(index, true));
        });

        this.querySelectorAll('[data-hotspot-toggle]').forEach((toggle) => {
          toggle.addEventListener('click', (event) => {
            event.stopPropagation();
            const hotspot = toggle.closest('[data-scene-hotspot]');
            if (!hotspot) return;

            if (this.mobileMedia.matches) {
              this.showMobileProduct(hotspot, toggle);
              return;
            }

            const willOpen = !hotspot.classList.contains('is-open');
            this.closeHotspots(hotspot);
            hotspot.classList.toggle('is-open', willOpen);
            toggle.setAttribute('aria-expanded', String(willOpen));
          });
        });

        this.querySelectorAll('[data-hotspot-card]').forEach((card) => {
          card._sceneHotspotOwner = card.closest('[data-scene-hotspot]');
          card.addEventListener('click', (event) => event.stopPropagation());
        });

        this.addEventListener('mouseenter', () => this.stopAutoplay());
        this.addEventListener('mouseleave', () => this.startAutoplay());
        this.addEventListener('focusin', () => this.stopAutoplay());
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.startAutoplay();
        });

        this.goTo(this.currentIndex, false);
        this.startAutoplay();
      }

      disconnectedCallback() {
        this.stopAutoplay();
        this.previousButton?.removeEventListener('click', this.onPrevious);
        this.nextButton?.removeEventListener('click', this.onNext);
        this.removeEventListener('keydown', this.onKeydown);
        this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
        this.viewport?.removeEventListener('pointerup', this.onPointerUp);
        this.viewport?.removeEventListener('pointercancel', this.onPointerUp);
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        this.mobileMedia?.removeEventListener?.('change', this.onBreakpointChange);
      }

      goTo(index, userInitiated = false) {
        if (!this.slides.length) return;

        this.currentIndex = (index + this.slides.length) % this.slides.length;
        this.track.style.transform = `translate3d(-${this.currentIndex * 100}%, 0, 0)`;
        this.restoreMobileCards();
        this.closeHotspots();

        this.slides.forEach((slide, slideIndex) => {
          const isActive = slideIndex === this.currentIndex;
          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-hidden', String(!isActive));
          slide.querySelectorAll('a, button, input, select, textarea').forEach((control) => {
            if (isActive) {
              control.removeAttribute('tabindex');
            } else {
              control.setAttribute('tabindex', '-1');
            }
          });
        });

        this.dots.forEach((dot, dotIndex) => {
          const isActive = dotIndex === this.currentIndex;
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-current', isActive ? 'true' : 'false');
        });

        if (this.status) {
          this.status.textContent = `${this.currentIndex + 1} / ${this.slides.length}`;
        }

        if (userInitiated) {
          this.stopAutoplay();
          this.startAutoplay();
        }
      }

      closeHotspots(exception) {
        this.querySelectorAll('[data-scene-hotspot].is-open').forEach((hotspot) => {
          if (hotspot === exception) return;
          hotspot.classList.remove('is-open');
          hotspot.querySelector('[data-hotspot-toggle]')?.setAttribute('aria-expanded', 'false');
        });
      }

      showMobileProduct(hotspot, toggle) {
        const slide = hotspot.closest('[data-scene-slide]');
        const panel = slide?.querySelector('[data-mobile-product]');
        const cardId = toggle.getAttribute('aria-controls');
        if (!slide || !panel || !cardId) return;

        this.restoreMobileCards(slide);
        this.closeHotspots();

        const card = this.querySelector(`#${CSS.escape(cardId)}`);
        if (!card) return;

        card._sceneHotspotOwner = hotspot;
        panel.appendChild(card);
        panel.classList.add('has-product');
        panel.setAttribute('aria-hidden', 'false');
        hotspot.classList.add('is-selected');
        toggle.setAttribute('aria-expanded', 'true');
        this.stopAutoplay();
      }

      restoreMobileCards(scope = this) {
        scope.querySelectorAll('[data-mobile-product]').forEach((panel) => {
          panel.querySelectorAll('[data-hotspot-card]').forEach((card) => {
            card._sceneHotspotOwner?.appendChild(card);
          });
          panel.classList.remove('has-product');
          panel.setAttribute('aria-hidden', 'true');
        });

        scope.querySelectorAll('[data-scene-hotspot].is-selected').forEach((hotspot) => {
          hotspot.classList.remove('is-selected');
          hotspot.querySelector('[data-hotspot-toggle]')?.setAttribute('aria-expanded', 'false');
        });
      }

      handleDocumentClick(event) {
        if (this.mobileMedia.matches) return;
        if (!this.contains(event.target) || !event.target.closest('[data-scene-hotspot]')) {
          this.closeHotspots();
        }
      }

      handleKeydown(event) {
        if (event.key === 'Escape') {
          this.restoreMobileCards();
          this.closeHotspots();
          return;
        }

        if (event.target.closest('[data-hotspot-card]')) return;
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.currentIndex - 1, true);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.currentIndex + 1, true);
        }
      }

      handlePointerDown(event) {
        if (event.pointerType === 'mouse' || event.target.closest('[data-scene-hotspot]')) return;
        this.pointerStartX = event.clientX;
      }

      handlePointerUp(event) {
        if (this.pointerStartX === null) return;
        const distance = event.clientX - this.pointerStartX;
        this.pointerStartX = null;
        if (Math.abs(distance) < 45) return;
        this.goTo(this.currentIndex + (distance < 0 ? 1 : -1), true);
      }

      handleVisibilityChange() {
        if (document.hidden) {
          this.stopAutoplay();
        } else {
          this.startAutoplay();
        }
      }

      handleBreakpointChange() {
        this.restoreMobileCards();
        this.closeHotspots();
      }

      handleBlockSelect(event) {
        if (!event.detail?.blockId || !this.dataset.sectionId) return;
        const block = this.querySelector(`[data-block-id="${CSS.escape(event.detail.blockId)}"]`);
        const slide = block?.closest('[data-scene-slide]');
        if (!slide) return;
        const index = this.slides.indexOf(slide);
        if (index >= 0) this.goTo(index, false);
        this.stopAutoplay();
      }

      startAutoplay() {
        if (!this.autoplayEnabled || this.slides.length < 2 || document.hidden) return;
        this.stopAutoplay();
        this.autoplayTimer = window.setInterval(() => this.goTo(this.currentIndex + 1), this.autoplayDelay);
      }

      stopAutoplay() {
        if (!this.autoplayTimer) return;
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }
  );
}
